/**
 * Credits, refunds and extensions for one customer (subscription engine,
 * phase 4; decision D7). Staff ask here; anything above one person's limit
 * waits in Billing approvals for someone else:
 *
 * - a credit above KES 5,000;
 * - every cash refund (then, once paid out, it's marked paid here with the
 *   payment's reference);
 * - a trial extension over 14 days, a due date or grace over 7, and every
 *   free-service extension.
 *
 * Changing a customer's money or dates: type their name to confirm.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Badge, Button, formatCurrency } from '@lipaflow/ui'
import { apiErrorMessage } from '@lipaflow/shared'
import {
  billingAdjustments,
  type CustomerCredit,
  type ExtensionKind,
  type Requested,
} from '../../services/billingApprovals'

const field = 'w-full rounded-button border border-hairline bg-surface p-2.5 text-body-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent'
const labelCls = 'block text-caption text-ink-muted mb-1'
const money = (n: number) => formatCurrency(n, true)
const day = (iso: string) =>
  new Date(iso).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })

const EXTENSIONS: { kind: ExtensionKind; label: string; limit: string }[] = [
  { kind: 'trial', label: 'Longer trial', limit: 'Up to 14 days applies at once' },
  { kind: 'due_date', label: 'Later due date', limit: 'Up to 7 days applies at once, once per invoice' },
  { kind: 'grace', label: 'Longer grace before read-only', limit: 'Up to 7 days applies at once' },
  { kind: 'service', label: 'Free service days', limit: 'Always needs a second person' },
]

const CREDIT_STATUS: Record<string, { label: string; variant: 'success' | 'warning' | 'neutral' | 'clay' }> = {
  available: { label: 'Available', variant: 'success' },
  pending_approval: { label: 'Waiting for approval', variant: 'warning' },
  rejected: { label: 'Rejected', variant: 'clay' },
  void: { label: 'Void', variant: 'neutral' },
}
const REFUND_STATUS: Record<string, { label: string; variant: 'success' | 'warning' | 'neutral' | 'clay' }> = {
  pending_approval: { label: 'Waiting for approval', variant: 'warning' },
  approved: { label: 'Approved: pay it out', variant: 'warning' },
  paid: { label: 'Paid', variant: 'success' },
  rejected: { label: 'Rejected', variant: 'clay' },
}

type Mode = 'view' | 'credit' | 'extend' | { refund: CustomerCredit } | { paid: string }

export function AdjustmentsSection({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<Mode>('view')
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [source, setSource] = useState<'goodwill' | 'manual'>('goodwill')
  const [kind, setKind] = useState<ExtensionKind>('trial')
  const [days, setDays] = useState(7)
  const [invoiceId, setInvoiceId] = useState('')
  const [reference, setReference] = useState('')
  const [confirmName, setConfirmName] = useState('')

  const data = useQuery({ queryKey: ['customer-adjustments', tenantId], queryFn: () => billingAdjustments.forCustomer(tenantId) })

  const reset = () => {
    setMode('view'); setAmount(''); setReason(''); setSource('goodwill'); setKind('trial'); setDays(7)
    setInvoiceId(''); setReference(''); setConfirmName('')
  }
  const done = (applied: string) => (result: Requested) => {
    toast.success(result.status === 'pending_approval' ? 'Asked: a second person decides it in Billing approvals' : applied)
    queryClient.invalidateQueries({ queryKey: ['customer-adjustments', tenantId] })
    queryClient.invalidateQueries({ queryKey: ['customer-subscription', tenantId] })
    queryClient.invalidateQueries({ queryKey: ['billing-approvals'] })
    reset()
  }
  const failed = (fallback: string) => (e: unknown) => toast.error(apiErrorMessage(e, fallback))

  const credit = useMutation({
    mutationFn: () => billingAdjustments.grantCredit(tenantId, { amount: Number(amount), reason: reason.trim(), source }),
    onSuccess: done('Credit given: it settles their next invoice'),
    onError: failed("Couldn't give the credit"),
  })
  const refund = useMutation({
    mutationFn: (creditId: string) => billingAdjustments.requestRefund(creditId, { amount: Number(amount), reason: reason.trim() }),
    onSuccess: done('Refund asked'),
    onError: failed("Couldn't ask for the refund"),
  })
  const paid = useMutation({
    mutationFn: (refundId: string) => billingAdjustments.markRefundPaid(refundId, reference.trim()),
    onSuccess: done('Refund marked paid'),
    onError: failed("Couldn't mark the refund paid"),
  })
  const extend = useMutation({
    mutationFn: () => billingAdjustments.requestExtension(tenantId, {
      kind, days, reason: reason.trim(), invoice_id: kind === 'due_date' || kind === 'grace' ? invoiceId : undefined,
    }),
    onSuccess: done('Extended'),
    onError: failed("Couldn't extend"),
  })

  if (data.isLoading) return <p role="status" className="text-body-sm text-ink-muted">Loading credits and refunds…</p>
  if (data.isError || !data.data) {
    return <p role="alert" className="text-body-sm text-warning">{apiErrorMessage(data.error, "Couldn't load credits and refunds.")}</p>
  }
  const { balance, credits, refunds, unpaid_invoices: unpaid } = data.data
  const confirmed = confirmName.trim().toLowerCase() === tenantName.trim().toLowerCase()
  const amountOk = Number(amount) > 0
  const reasonOk = reason.trim().length >= 3
  const needsInvoice = kind === 'due_date' || kind === 'grace'

  const Confirm = (
    <label className="block">
      <span className={labelCls}>Type <span className="text-ink font-medium">{tenantName}</span> to confirm</span>
      <input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} className={field} autoComplete="off" aria-label="Type the customer's name to confirm" />
    </label>
  )
  const Reason = (placeholder: string) => (
    <label className="block"><span className={labelCls}>Why</span>
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={placeholder} className={field} />
    </label>
  )
  const actions = ({ label, onClick, busy, ready }: { label: string; onClick: () => void; busy: boolean; ready: boolean }) => (
    <div className="flex gap-2">
      <Button variant="secondary" onClick={reset} disabled={busy}>Cancel</Button>
      <Button onClick={onClick} isLoading={busy} disabled={!ready}>{label}</Button>
    </div>
  )

  return (
    <section aria-labelledby="customer-adjustments" className="rounded-card border border-hairline p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 id="customer-adjustments" className="font-inter text-caption text-ink-muted">Credits, refunds and extensions</h4>
        <span className="text-body-sm text-ink">Credit to use: <span className="font-medium">{money(balance)}</span></span>
      </div>

      {credits.length > 0 && (
        <ul className="space-y-2" aria-label="Credits">
          {credits.map((c) => {
            const status = CREDIT_STATUS[c.status] ?? { label: c.status, variant: 'neutral' as const }
            return (
              <li key={c.id} className="flex flex-wrap items-start justify-between gap-2 rounded-button bg-surface-alt px-3 py-2">
                <div>
                  <p className="text-body-sm text-ink">
                    {money(c.amount)} credit{c.source === 'manual' ? ' (manual)' : ''}
                    {c.status === 'available' && c.remaining !== c.amount && <span className="text-ink-muted"> · {money(c.remaining)} left</span>}
                  </p>
                  <p className="text-caption text-ink-muted">{c.reason}{c.created_at ? ` · ${day(c.created_at)}` : ''}</p>
                </div>
                <span className="flex items-center gap-2">
                  <Badge variant={status.variant} size="sm">{status.label}</Badge>
                  {c.status === 'available' && c.remaining > 0 && mode === 'view' && (
                    <Button size="sm" variant="ghost" onClick={() => { setMode({ refund: c }); setAmount(String(c.remaining)) }}>Refund</Button>
                  )}
                </span>
              </li>
            )
          })}
        </ul>
      )}

      {refunds.length > 0 && (
        <ul className="space-y-2" aria-label="Refunds">
          {refunds.map((r) => {
            const status = REFUND_STATUS[r.status] ?? { label: r.status, variant: 'neutral' as const }
            return (
              <li key={r.id} className="flex flex-wrap items-start justify-between gap-2 rounded-button bg-surface-alt px-3 py-2">
                <div>
                  <p className="text-body-sm text-ink">{money(r.amount)} cash refund</p>
                  <p className="text-caption text-ink-muted">{r.reason}{r.payment_reference ? ` · paid, ref ${r.payment_reference}` : ''}</p>
                </div>
                <span className="flex items-center gap-2">
                  <Badge variant={status.variant} size="sm">{status.label}</Badge>
                  {r.status === 'approved' && mode === 'view' && (
                    <Button size="sm" variant="ghost" onClick={() => setMode({ paid: r.id })}>Mark paid</Button>
                  )}
                </span>
              </li>
            )
          })}
        </ul>
      )}

      {credits.length === 0 && refunds.length === 0 && mode === 'view' && (
        <p className="text-body-sm text-ink-muted">No credits or refunds.</p>
      )}

      {mode === 'view' && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-hairline">
          <Button size="sm" variant="secondary" onClick={() => setMode('credit')}>Give credit</Button>
          <Button size="sm" variant="secondary" onClick={() => setMode('extend')}>Extend</Button>
        </div>
      )}

      {mode === 'credit' && (
        <div className="space-y-3 pt-2 border-t border-hairline">
          <p className="text-caption text-ink-muted">It settles their next invoice. Above KES 5,000, someone else approves it first.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block"><span className={labelCls}>Amount (KES)</span>
              <input type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} className={field} />
            </label>
            <label className="block"><span className={labelCls}>Kind</span>
              <select value={source} onChange={(e) => setSource(e.target.value as 'goodwill' | 'manual')} className={field}>
                <option value="goodwill">Goodwill (we're making up for something)</option>
                <option value="manual">Manual (correcting a billing mistake)</option>
              </select>
            </label>
          </div>
          {Reason('e.g. Two days of downtime in March')}
          {Confirm}
          {actions({ label: 'Give credit', onClick: () => credit.mutate(), busy: credit.isPending, ready: amountOk && reasonOk && confirmed })}
        </div>
      )}

      {typeof mode === 'object' && 'refund' in mode && (
        <div className="space-y-3 pt-2 border-t border-hairline">
          <p className="text-caption text-ink-muted">
            Cash back from this credit ({money(mode.refund.remaining)} left). Someone else approves it; then you pay it out and mark it paid.
          </p>
          <label className="block"><span className={labelCls}>Amount (KES)</span>
            <input type="number" min={1} max={mode.refund.remaining} value={amount} onChange={(e) => setAmount(e.target.value)} className={field} />
          </label>
          {Reason('e.g. They are leaving and asked for the balance')}
          {Confirm}
          {actions({
            label: 'Ask for the refund',
            onClick: () => refund.mutate(mode.refund.id),
            busy: refund.isPending,
            ready: amountOk && Number(amount) <= mode.refund.remaining && reasonOk && confirmed,
          })}
        </div>
      )}

      {typeof mode === 'object' && 'paid' in mode && (
        <div className="space-y-3 pt-2 border-t border-hairline">
          <label className="block"><span className={labelCls}>Payment reference (M-PESA code or bank reference)</span>
            <input value={reference} onChange={(e) => setReference(e.target.value)} className={field} autoComplete="off" />
          </label>
          {actions({ label: 'Mark paid', onClick: () => paid.mutate(mode.paid), busy: paid.isPending, ready: reference.trim().length >= 3 })}
        </div>
      )}

      {mode === 'extend' && (
        <div className="space-y-3 pt-2 border-t border-hairline">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block"><span className={labelCls}>What</span>
              <select value={kind} onChange={(e) => setKind(e.target.value as ExtensionKind)} className={field}>
                {EXTENSIONS.map((x) => <option key={x.kind} value={x.kind}>{x.label}</option>)}
              </select>
            </label>
            <label className="block"><span className={labelCls}>Days</span>
              <input type="number" min={1} max={180} value={days} onChange={(e) => setDays(Number(e.target.value))} className={field} />
            </label>
          </div>
          <p className="text-caption text-ink-muted">{EXTENSIONS.find((x) => x.kind === kind)?.limit}; longer waits for a second person.</p>
          {needsInvoice && (
            unpaid.length === 0
              ? <p className="text-body-sm text-warning">They have no unpaid invoice to extend.</p>
              : (
                <label className="block"><span className={labelCls}>Invoice</span>
                  <select value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)} className={field}>
                    <option value="">Choose…</option>
                    {unpaid.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.number} · {money(i.amount - i.paid_amount)} due{i.due_date ? ` ${day(i.due_date)}` : ''}
                      </option>
                    ))}
                  </select>
                </label>
              )
          )}
          {Reason('e.g. Waiting for their board to approve payment')}
          {Confirm}
          {actions({
            label: `Extend by ${days} days`,
            onClick: () => extend.mutate(),
            busy: extend.isPending,
            ready: days >= 1 && days <= 180 && reasonOk && confirmed && (!needsInvoice || Boolean(invoiceId)),
          })}
        </div>
      )}
    </section>
  )
}
